import { useMemo } from 'react';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Box, PanelHeadingText, Surface, SurfaceButton, useTheme } from '@/ui/primitives';
import { StatusMetricCardFooter } from './StatusMetricCardFooter';
import { buildMetricCardGridColumns } from './StatusMetricCardGridLayout';
import { StatusMetricCardsGridSkeletons } from './StatusMetricCardsGridSkeletons';
import { StatusMetricCardValueRow } from './StatusMetricCardValueRow';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ElementType, ReactElement } from 'react';

type StatusMetricCardsGridProps = {
  metricCards: MetricCardItem[];
  loading?: boolean;
  skeletonCount?: number;
  comparisonWindowLabel: string | null;
  showNoChangePlaceholder?: boolean;
  resolveStatusCardIcon: (statusId: string) => ElementType;
  resolveStatusCardIconColor: (statusTone: MetricCardItem['statusTone']) => string;
  selectedIds?: string[];
  onCardToggle?: (statusId: string) => void;
};

export const StatusMetricCardsGrid = ({
  metricCards,
  loading = false,
  skeletonCount,
  comparisonWindowLabel,
  showNoChangePlaceholder = true,
  resolveStatusCardIcon,
  resolveStatusCardIconColor,
  selectedIds,
  onCardToggle,
}: StatusMetricCardsGridProps): ReactElement => {
  const theme = useTheme();
  const accentColor = resolveSemanticColor(theme, 'accent');
  const selectedSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds]);

  if (loading && metricCards.length === 0) {
    return (
      <StatusMetricCardsGridSkeletons count={skeletonCount ?? Math.max(metricCards.length, 5)} />
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: buildMetricCardGridColumns(),
        gridAutoRows: '1fr',
        columnGap: s('xl'),
        rowGap: s('xl'),
      }}
    >
      {metricCards.map((card, index) => {
        const countDelta = card.countDelta ?? card.delta;
        const countDeltaTone = card.countDeltaTone ?? card.deltaTone;
        const isSelectable = onCardToggle !== undefined && card.id !== 'total';
        const isSelected = selectedSet.has(card.id);
        const hasCountDelta =
          countDelta !== null &&
          countDelta !== undefined &&
          countDelta !== '' &&
          countDelta !== '-';
        const hasPercentageDelta =
          card.percentageDelta !== null &&
          card.percentageDelta !== undefined &&
          card.percentageDelta !== '' &&
          card.percentageDelta !== '-';
        const hasDelta = hasCountDelta || hasPercentageDelta;
        const showFooter =
          hasDelta ||
          Boolean(comparisonWindowLabel) ||
          (!comparisonWindowLabel && showNoChangePlaceholder);
        const resolvedDeltaTone = countDeltaTone ?? card.percentageDeltaTone;

        const cardContent = (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'stretch',
              minWidth: 0,
              px: { xs: s('md'), xl: s('lg') },
              pt: s('sm'),
              pb: showFooter ? s('md') : s('sm'),
            }}
          >
            <Box
              sx={{
                width: '100%',
                minWidth: 0,
                display: 'grid',
                gridTemplateRows: showFooter ? 'auto auto auto' : 'auto auto',
                rowGap: s('none'),
                alignItems: 'center',
                alignContent: 'start',
              }}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) auto',
                  alignItems: 'center',
                  columnGap: s('xxs'),
                  pl: s('xs'),
                  minWidth: 0,
                }}
              >
                <PanelHeadingText sx={{ minWidth: 0 }}>{card.status}</PanelHeadingText>
                <Box
                  component={resolveStatusCardIcon(card.id)}
                  sx={{
                    fontSize: s('2xl'),
                    color: resolveStatusCardIconColor(card.statusTone),
                    justifySelf: 'end',
                    flexShrink: 0,
                    mt: s('xxs'),
                  }}
                />
              </Box>
              <Box sx={{ pb: showFooter ? s('xs') : s('none'), minWidth: 0 }}>
                <StatusMetricCardValueRow
                  count={card.count}
                  percentage={card.percentage}
                  secondaryValue={card.secondaryValue}
                />
              </Box>
              {showFooter ? (
                <StatusMetricCardFooter
                  comparisonWindowLabel={comparisonWindowLabel}
                  countDelta={countDelta}
                  countDeltaTone={resolvedDeltaTone}
                  percentageDelta={card.percentageDelta}
                  percentageDeltaTone={card.percentageDeltaTone}
                  showNoChangePlaceholder={showNoChangePlaceholder}
                />
              ) : null}
            </Box>
          </Box>
        );

        if (isSelectable) {
          return (
            <SurfaceButton
              key={`${card.id}-${String(index)}`}
              type="button"
              onClick={() => {
                onCardToggle(card.id);
              }}
              aria-pressed={isSelected}
              padding="none"
              sx={[
                {
                  height: '100%',
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: `2px solid ${accentColor}`,
                    outlineOffset: s('xxs'),
                  },
                },
                isSelected
                  ? {
                      outline: `1px solid ${accentColor}`,
                      outlineOffset: '-1px',
                    }
                  : null,
              ]}
            >
              {cardContent}
            </SurfaceButton>
          );
        }

        return (
          <Surface
            key={`${card.id}-${String(index)}`}
            padding="none"
            sx={{
              height: '100%',
              minWidth: 0,
            }}
          >
            {cardContent}
          </Surface>
        );
      })}
    </Box>
  );
};
